// Scheduling Assistant — staged batch auto-assign (Brevity-parity, Rostery design).
// Three-screen wizard: find unstaffed shifts → pick ranking rules → review board → confirm.
// Nothing is written to the live roster until Confirm (POST /scheduling-assistant/runs/:id/confirm).
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format, startOfWeek, addDays } from 'date-fns';
import {
  X, Loader2, ListChecks, Search, Check, ArrowLeft, ArrowRight, Trash2,
  Users, Repeat, DollarSign, ChevronUp, ChevronDown, RefreshCw, Ban, ShieldAlert,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const RULE_META = {
  previously_did: { icon: Users, label: 'Previously did shift', desc: 'Prioritise workers who have completed shifts for this client (continuity of care).' },
  equal_hours:    { icon: Repeat, label: 'Equal hours this run', desc: 'Spread the selected shifts fairly — workers with fewer hours assigned in THIS batch rank higher.' },
  lowest_cost:    { icon: DollarSign, label: 'Lowest actual cost', desc: 'Rank by the real SCHADS cost of each shift (after-hours/weekend/public-holiday loadings), not base rate.' },
};
const DEFAULT_RULES = ['previously_did', 'equal_hours', 'lowest_cost'];
const money = (n) => n == null ? '—' : `$${Number(n).toFixed(2)}`;

function StepDots({ step }) {
  const labels = ['Find shifts', 'Ranking rules', 'Review & confirm'];
  return (
    <div className="flex items-center gap-2">
      {labels.map((l, i) => {
        const n = i + 1, active = n === step, done = n < step;
        return (
          <div key={l} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${active ? 'bg-violet-600 text-white' : done ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-400'}`}>
              <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${active ? 'bg-white/25' : done ? 'bg-violet-200' : 'bg-slate-200'}`}>{done ? '✓' : n}</span>
              {l}
            </div>
            {i < labels.length - 1 && <div className="w-4 h-px bg-slate-200" />}
          </div>
        );
      })}
    </div>
  );
}

export default function SchedulingAssistantModal({ currentDate, onClose, onDone }) {
  const wkStart = startOfWeek(currentDate || new Date(), { weekStartsOn: 1 });
  const [step, setStep] = useState(1);
  const [from, setFrom] = useState(format(wkStart, 'yyyy-MM-dd'));
  const [to, setTo] = useState(format(addDays(wkStart, 6), 'yyyy-MM-dd'));
  const [unstaffed, setUnstaffed] = useState(null);          // null=not searched, []=searched empty
  const [selectedShiftIds, setSelectedShiftIds] = useState(new Set());
  const [rules, setRules] = useState([...DEFAULT_RULES]);
  const [run, setRun] = useState(null);                      // staged board
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [swapFor, setSwapFor] = useState(null);              // assignmentId whose dropdown is open
  const [swapAlts, setSwapAlts] = useState({});             // assignmentId → [alts]
  const [swapBusy, setSwapBusy] = useState(false);

  // ── Screen 1 ──
  const findShifts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/scheduling-assistant/unstaffed?from=${from}&to=${to}`);
      setUnstaffed(data.shifts || []);
      setSelectedShiftIds(new Set((data.shifts || []).map(s => s.id)));
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to find shifts'); }
    finally { setLoading(false); }
  };
  const toggleShift = (id) => setSelectedShiftIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── Screen 2 ──
  const toggleRule = (key) => setRules(prev => prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]);
  const moveRule = (key, dir) => setRules(prev => {
    const i = prev.indexOf(key); if (i < 0) return prev;
    const j = i + dir; if (j < 0 || j >= prev.length) return prev;
    const n = [...prev]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const buildRoster = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/scheduling-assistant/runs', {
        fromDate: from, toDate: to, shiftIds: [...selectedShiftIds], rankingRules: rules,
      });
      setRun(data); setStep(3);
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to build staged roster'); }
    finally { setLoading(false); }
  };

  // ── Screen 3 ──
  const patchSlot = (assignmentId, newSlot, newSummary) => {
    setRun(prev => ({
      ...prev,
      summary: newSummary || prev.summary,
      board: prev.board.map(b => ({ ...b, slots: b.slots.map(s => s.assignmentId === assignmentId ? { ...s, ...newSlot } : s) })),
    }));
  };
  const openSwap = async (slot) => {
    if (swapFor === slot.assignmentId) { setSwapFor(null); return; }
    setSwapFor(slot.assignmentId);
    if (slot.alternatives?.length) { setSwapAlts(a => ({ ...a, [slot.assignmentId]: slot.alternatives })); return; }
    try {
      const { data } = await api.patch(`/scheduling-assistant/assignments/${slot.assignmentId}?alternatives=1`, {});
      setSwapAlts(a => ({ ...a, [slot.assignmentId]: data.alternatives || [] }));
    } catch { setSwapAlts(a => ({ ...a, [slot.assignmentId]: [] })); }
  };
  const doSwap = async (slot, staffId) => {
    setSwapBusy(true);
    try {
      const { data } = await api.patch(`/scheduling-assistant/assignments/${slot.assignmentId}`, { staffId });
      patchSlot(slot.assignmentId, { staffId: data.slot.staffId, staffName: data.slot.staffName, estimatedCost: data.slot.estimatedCost, reason: data.slot.reason }, data.summary);
      setSwapFor(null);
    } catch (e) {
      if (e.response?.status === 409) toast.error(e.response.data.reason ? `Can't assign: ${e.response.data.reason}` : 'Worker not eligible');
      else toast.error('Swap failed');
    } finally { setSwapBusy(false); }
  };
  const removeSlot = async (slot) => {
    try {
      const { data } = await api.patch(`/scheduling-assistant/assignments/${slot.assignmentId}`, { staffId: null });
      patchSlot(slot.assignmentId, { staffId: null, staffName: null, estimatedCost: null, reason: null }, data.summary);
    } catch { toast.error('Failed to remove'); }
  };
  const discard = async () => {
    try { await api.delete(`/scheduling-assistant/runs/${run.runId}`); toast.success('Staged roster discarded'); onClose(); }
    catch { toast.error('Failed to discard'); }
  };
  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/scheduling-assistant/runs/${run.runId}/confirm`, {});
      const sk = data.skipped?.length || 0;
      toast.success(`Assigned ${data.committed} shift${data.committed === 1 ? '' : 's'}${sk ? `, ${sk} skipped` : ''}`);
      if (sk) console.warn('Scheduling Assistant skipped:', data.skipped);
      onDone();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to confirm'); }
    finally { setConfirming(false); }
  };

  const s = run?.summary;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b border-slate-100">
          <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center shrink-0"><ListChecks size={15} className="text-white" /></div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-800">Scheduling Assistant</h3>
            <p className="text-xs text-slate-400">Stage suggested assignments — nothing is booked until you confirm.</p>
          </div>
          <StepDots step={step} />
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-400 ml-2"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">

          {/* ── Step 1: Find unstaffed shifts ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4 max-w-md">
                <div><label className="block text-xs font-medium text-slate-600 mb-1">From</label><input type="date" value={from} onChange={e => setFrom(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm" /></div>
                <div><label className="block text-xs font-medium text-slate-600 mb-1">To</label><input type="date" value={to} onChange={e => setTo(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm" /></div>
              </div>
              <button onClick={findShifts} disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />} Find unstaffed shifts
              </button>

              {unstaffed && unstaffed.length === 0 && <p className="text-center text-slate-400 py-10 text-sm">No unstaffed shifts in this range.</p>}
              {unstaffed && unstaffed.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">{selectedShiftIds.size} of {unstaffed.length} selected</p>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedShiftIds(new Set(unstaffed.map(x => x.id)))} className="text-xs text-violet-600 hover:underline">All</button>
                      <button onClick={() => setSelectedShiftIds(new Set())} className="text-xs text-slate-500 hover:underline">None</button>
                    </div>
                  </div>
                  {unstaffed.map(sh => (
                    <label key={sh.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedShiftIds.has(sh.id) ? 'border-violet-300 bg-violet-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                      <input type="checkbox" checked={selectedShiftIds.has(sh.id)} onChange={() => toggleShift(sh.id)} className="rounded accent-violet-600" />
                      <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-slate-800">{sh.clientNames}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{sh.shiftType}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-mono">{sh.dow} {sh.date}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{sh.startTime}–{sh.endTime}</span>
                        <span className="text-xs text-slate-500">{sh.hours}h</span>
                        {sh.minStaff > 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">needs {sh.minStaff} staff</span>}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Step 2: Ranking rules ── */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-600">Choose how to rank eligible workers. Rules <span className="font-semibold">stack top-to-bottom</span> — the first rule decides, ties fall through to the next.</p>
              <div className="space-y-2">
                {rules.map((key, idx) => {
                  const m = RULE_META[key]; const Icon = m.icon;
                  return (
                    <div key={key} className="flex items-start gap-3 p-3 rounded-xl border border-violet-300 bg-violet-50">
                      <div className="flex flex-col gap-0.5 pt-0.5">
                        <button onClick={() => moveRule(key, -1)} disabled={idx === 0} className="text-slate-400 hover:text-violet-600 disabled:opacity-30"><ChevronUp size={14} /></button>
                        <button onClick={() => moveRule(key, 1)} disabled={idx === rules.length - 1} className="text-slate-400 hover:text-violet-600 disabled:opacity-30"><ChevronDown size={14} /></button>
                      </div>
                      <div className="w-7 h-7 rounded-lg bg-violet-600 flex items-center justify-center shrink-0"><Icon size={14} className="text-white" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2"><span className="text-xs font-bold text-violet-700">#{idx + 1}</span><span className="text-sm font-semibold text-slate-800">{m.label}</span></div>
                        <p className="text-xs text-slate-500 mt-0.5">{m.desc}</p>
                      </div>
                      <button onClick={() => toggleRule(key)} className="text-xs text-slate-400 hover:text-red-500 px-2 py-1">Remove</button>
                    </div>
                  );
                })}
                {DEFAULT_RULES.filter(k => !rules.includes(k)).map(key => {
                  const m = RULE_META[key]; const Icon = m.icon;
                  return (
                    <div key={key} className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-slate-200 bg-white opacity-70">
                      <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon size={14} className="text-slate-400" /></div>
                      <div className="flex-1 min-w-0"><span className="text-sm font-medium text-slate-500">{m.label}</span><p className="text-xs text-slate-400 mt-0.5">{m.desc}</p></div>
                      <button onClick={() => toggleRule(key)} className="text-xs text-violet-600 hover:underline px-2 py-1">Add</button>
                    </div>
                  );
                })}
                {rules.length === 0 && <p className="text-xs text-amber-600">Add at least one rule, or all three default rules will be used.</p>}
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 mb-2"><ShieldAlert size={14} className="text-slate-500" /><span className="text-xs font-semibold text-slate-700">Automatic exclusions (applied before ranking)</span></div>
                <ul className="text-xs text-slate-500 grid grid-cols-2 gap-x-4 gap-y-1">
                  <li>• Inactive / on-hold workers</li>
                  <li>• Scheduling clash (incl. within this batch)</li>
                  <li>• Approved leave</li>
                  <li>• Dated unavailability</li>
                  <li>• Weekday unavailability</li>
                  <li>• Client-incompatible workers</li>
                </ul>
                <p className="text-[11px] text-slate-400 mt-2">Each shift's minimum staff count is respected — shifts needing more than one worker get multiple slots.</p>
              </div>
            </div>
          )}

          {/* ── Step 3: Review board ── */}
          {step === 3 && run && (
            <div className="space-y-3">
              {s && (
                <div className="flex flex-wrap gap-2 mb-1">
                  <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700 font-semibold">{s.shifts} shifts</span>
                  <span className="text-xs px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold">{s.filled} filled</span>
                  {s.unfilled > 0 && <span className="text-xs px-3 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold">{s.unfilled} unfilled</span>}
                  <span className="text-xs px-3 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold">{money(s.totalEstimatedCost)} est. cost</span>
                </div>
              )}
              {run.board.map(b => (
                <div key={b.shiftId} className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="flex items-center gap-2 flex-wrap px-4 py-2.5 bg-slate-50 border-b border-slate-100">
                    <span className="text-sm font-semibold text-slate-800">{b.clientNames}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200">{b.shiftType}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-white text-slate-600 border border-slate-200 font-mono">{b.date}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{b.startTime}–{b.endTime}</span>
                    <span className="text-xs text-slate-500">{b.hours}h</span>
                    {b.minStaff > 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">{b.minStaff} staff</span>}
                  </div>
                  <div className="divide-y divide-slate-50">
                    {b.slots.map(slot => (
                      <div key={slot.assignmentId} className="px-4 py-2.5">
                        <div className="flex items-center gap-3">
                          {b.minStaff > 1 && <span className="text-[11px] font-bold text-slate-400 w-10">Slot {slot.slotIndex + 1}</span>}
                          {slot.staffId ? (
                            <>
                              <div className="flex-1 min-w-0">
                                <span className="text-sm font-medium text-slate-800">{slot.staffName}</span>
                                {slot.reason && <p className="text-xs text-slate-400 mt-0.5">{slot.reason}</p>}
                              </div>
                              <span className="text-sm font-semibold text-slate-700">{money(slot.estimatedCost)}</span>
                              <button onClick={() => openSwap(slot)} className="flex items-center gap-1 text-xs text-violet-600 hover:bg-violet-50 px-2 py-1 rounded-lg"><RefreshCw size={12} /> Swap</button>
                              <button onClick={() => removeSlot(slot)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 px-2 py-1 rounded-lg"><Trash2 size={12} /></button>
                            </>
                          ) : (
                            <>
                              <div className="flex-1 flex items-center gap-2">
                                <Ban size={14} className="text-amber-500" />
                                <span className="text-sm text-amber-700">Unfilled{slot.excludedCount ? ` — ${slot.excludedCount} excluded` : ' — no eligible worker'}</span>
                              </div>
                              <button onClick={() => openSwap(slot)} className="flex items-center gap-1 text-xs text-violet-600 hover:bg-violet-50 px-2 py-1 rounded-lg"><RefreshCw size={12} /> Assign</button>
                            </>
                          )}
                        </div>
                        {swapFor === slot.assignmentId && (
                          <div className="mt-2 ml-1 rounded-lg border border-violet-100 bg-violet-50/50 p-2 space-y-1">
                            {!swapAlts[slot.assignmentId] && <div className="flex items-center gap-2 text-xs text-slate-400 px-2 py-1"><Loader2 size={12} className="animate-spin" /> Finding eligible workers…</div>}
                            {swapAlts[slot.assignmentId]?.length === 0 && <p className="text-xs text-slate-400 px-2 py-1">No other eligible workers.</p>}
                            {swapAlts[slot.assignmentId]?.filter(a => a.staffId !== slot.staffId).map(a => (
                              <button key={a.staffId} onClick={() => doSwap(slot, a.staffId)} disabled={swapBusy}
                                className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-white text-sm disabled:opacity-50">
                                <span className="font-medium text-slate-700 flex-1">{a.staffName}</span>
                                <span className="text-xs text-slate-400">{a.reason}</span>
                                <Check size={13} className="text-violet-500" />
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100">
          <div>
            {step === 3 && <button onClick={discard} className="flex items-center gap-1.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl"><Trash2 size={14} /> Discard</button>}
          </div>
          <div className="flex gap-3">
            {step > 1 && <button onClick={() => setStep(step - 1)} className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 rounded-xl text-sm text-slate-600 hover:bg-slate-50"><ArrowLeft size={14} /> Back</button>}
            {step === 1 && (
              <button onClick={() => setStep(2)} disabled={selectedShiftIds.size === 0}
                className="flex items-center gap-1.5 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                Continue <ArrowRight size={14} />
              </button>
            )}
            {step === 2 && (
              <button onClick={buildRoster} disabled={loading}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <ListChecks size={14} />} View suggestions
              </button>
            )}
            {step === 3 && (
              <button onClick={confirm} disabled={confirming || !s?.filled}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-semibold disabled:opacity-60">
                {confirming ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Confirm & assign {s?.filled || 0} shift{s?.filled === 1 ? '' : 's'}
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
